import { VOICE_CONFIG } from "../../types/audio";
import type { SttBackend, SttRecognitionOptions } from "./types";

interface WebSpeechResult {
  readonly isFinal: boolean;
  readonly length: number;
  readonly [index: number]: { readonly transcript: string };
}

interface WebSpeechResultList {
  readonly length: number;
  readonly [index: number]: WebSpeechResult;
}

interface WebSpeechRecognitionEvent extends Event {
  readonly resultIndex: number;
  readonly results: WebSpeechResultList;
}

interface WebSpeechErrorEvent extends Event {
  readonly error: string;
  readonly message: string;
}

interface WebSpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  onresult: ((this: WebSpeechRecognition, ev: WebSpeechRecognitionEvent) => void) | null;
  onerror: ((this: WebSpeechRecognition, ev: WebSpeechErrorEvent) => void) | null;
  onend: ((this: WebSpeechRecognition, ev: Event) => void) | null;
  onstart: ((this: WebSpeechRecognition, ev: Event) => void) | null;
  start(): void;
  stop(): void;
  abort(): void;
}

interface WebSpeechRecognitionCtor {
  new (): WebSpeechRecognition;
}

declare global {
  interface Window {
    SpeechRecognition?: WebSpeechRecognitionCtor;
    webkitSpeechRecognition?: WebSpeechRecognitionCtor;
  }
}

function getCtor(): WebSpeechRecognitionCtor | null {
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

let activeRecognition: WebSpeechRecognition | null = null;
let silenceTimer: ReturnType<typeof setTimeout> | null = null;
let noSpeechTimer: ReturnType<typeof setTimeout> | null = null;

function clearSilenceTimer(): void {
  if (silenceTimer) {
    clearTimeout(silenceTimer);
    silenceTimer = null;
  }
}

function clearNoSpeechTimer(): void {
  if (noSpeechTimer) {
    clearTimeout(noSpeechTimer);
    noSpeechTimer = null;
  }
}

function clearAllTimers(): void {
  clearSilenceTimer();
  clearNoSpeechTimer();
}

export const webSpeechSttBackend: SttBackend = {
  name: "web-speech",

  isSupported(): Promise<boolean> {
    return Promise.resolve(getCtor() !== null);
  },

  startRecognition(opts: SttRecognitionOptions): Promise<void> {
    const Ctor = getCtor();
    if (!Ctor) {
      const err = new Error("Web Speech API not available in this browser");
      opts.onError?.(err);
      throw err;
    }

    if (activeRecognition) {
      activeRecognition.abort();
      activeRecognition = null;
      clearAllTimers();
    }

    const recognition = new Ctor();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = VOICE_CONFIG[opts.language].locale;

    const noSpeechTimeout = opts.noSpeechTimeout ?? Math.max(opts.silenceTimeout, 10000);

    const endViaTimeout = (): void => {
      timedOut = true;
      recognition.stop();
    };

    const armNoSpeechTimer = (): void => {
      clearNoSpeechTimer();
      noSpeechTimer = setTimeout(endViaTimeout, noSpeechTimeout);
    };

    const armSilenceTimer = (): void => {
      clearSilenceTimer();
      silenceTimer = setTimeout(endViaTimeout, opts.silenceTimeout);
    };

    let timedOut = false;

    recognition.onstart = () => {
      armNoSpeechTimer();
      opts.onStart?.();
    };

    recognition.onresult = (event) => {
      clearNoSpeechTimer();
      armSilenceTimer();
      let interim = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const text = result[0]?.transcript ?? "";
        if (result.isFinal) {
          if (text.trim()) opts.onFinalResult?.(text);
        } else {
          interim += text;
        }
      }
      if (interim) opts.onInterimResult?.(interim);
    };

    recognition.onerror = (event) => {
      if (event.error === "no-speech" || event.error === "aborted") return;
      const err = new Error(`Web Speech recognition error: ${event.error}`);
      opts.onError?.(err);
    };

    recognition.onend = () => {
      clearAllTimers();
      activeRecognition = null;
      opts.onEnd?.({ viaTimeout: timedOut });
    };

    activeRecognition = recognition;

    try {
      recognition.start();
    } catch (err) {
      activeRecognition = null;
      clearAllTimers();
      const error = err instanceof Error ? err : new Error(String(err));
      opts.onError?.(error);
      throw error;
    }

    return Promise.resolve();
  },

  stopRecognition(): Promise<void> {
    clearAllTimers();
    if (activeRecognition) {
      activeRecognition.stop();
      activeRecognition = null;
    }
    return Promise.resolve();
  },
};
